import { useEffect, useId, useRef, useState } from 'react';

import styled from '@emotion/styled';

interface InfoTipProps {
  children: React.ReactNode;
  label?: string;
}

// A small ⓘ beside a label that explains it in plain words (Apple's inline
// help): opens on hover, keyboard focus or tap; Escape or a click away closes.
const InfoTip: React.FC<InfoTipProps> = ({ children, label = 'What does this mean?' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <Wrap ref={ref} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Trigger
        type='button'
        aria-label={label}
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        i
      </Trigger>
      {open && (
        <Bubble role='tooltip' id={id}>
          {children}
        </Bubble>
      )}
    </Wrap>
  );
};

export default InfoTip;

const Wrap = styled.span`
  position: relative;
  display: inline-flex;
  vertical-align: middle;
  margin-left: 6px;
`;

const Trigger = styled.button`
  display: inline-grid;
  place-items: center;
  width: 15px;
  height: 15px;
  padding: 0;
  border-radius: 999px;
  border: 1px solid #c9c4b6;
  background: transparent;
  color: #7a8279;
  font: italic 600 10px/1 Georgia, 'Times New Roman', serif;
  cursor: help;
  transition:
    color 0.15s ease,
    border-color 0.15s ease;

  &:hover,
  &[aria-expanded='true'] {
    color: var(--ink);
    border-color: var(--ink);
  }
`;

const Bubble = styled.span`
  position: absolute;
  z-index: 50;
  left: -10px;
  bottom: calc(100% + 8px);
  width: max-content;
  max-width: 260px;
  padding: 9px 11px;
  border-radius: 9px;
  background: #0e1a13;
  color: #eef1ea;
  font-size: 12.5px;
  font-weight: 400;
  line-height: 1.45;
  text-transform: none;
  letter-spacing: normal;
  white-space: normal;
  text-align: left;
  box-shadow: 0 10px 28px -10px rgba(14, 26, 19, 0.5);
  animation: tipIn 0.12s ease-out;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 12px;
    border: 5px solid transparent;
    border-top-color: #0e1a13;
  }

  @keyframes tipIn {
    from {
      opacity: 0;
      transform: translateY(3px);
    }
  }
`;
